import * as React from "react";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import { defaultCompare } from "logic/DefaultCompare";
import Select from "components/common/Select";
import FieldTypeIcon from "views/components/sidebar/fields/FieldTypeIcon";
import { Button } from "components/bootstrap";
const FieldName = styled.span`
  display: inline-flex;
  gap: 2px;
  align-items: center;
`;
const ButtonRow = styled.div`
  margin-top: 5px;
  display: inline-flex;
  gap: 5px;
  margin-bottom: 10px;
`;
const sortByLabel = ({ label: label1 }, { label: label2 }) => defaultCompare(label1, label2);
const UnqualifiedOption = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.gray[70]};
  `
);
const OptionRenderer = ({ label, qualified, type = void 0 }) => {
  const children = /* @__PURE__ */ React.createElement(FieldName, null, type && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FieldTypeIcon, { type }), " "), label);
  return qualified ? /* @__PURE__ */ React.createElement("span", null, children) : /* @__PURE__ */ React.createElement(UnqualifiedOption, null, children);
};
const FieldSelect = ({
  ariaLabel = void 0,
  autoFocus = void 0,
  allowCreate = false,
  className = void 0,
  clearable = false,
  excludedFields = [],
  id,
  isFieldQualified = () => true,
  isLoading = void 0,
  name,
  onChange,
  onMenuClose = void 0,
  openMenuOnFocus = void 0,
  persistSelection = void 0,
  placeholder = void 0,
  selectRef = void 0,
  size = "small",
  value,
  onSelectAllRest = void 0,
  showSelectAllRest = false,
  onDeSelectAll = void 0,
  showDeSelectAll = false,
  options
}) => {
  const { t } = useTranslation("aggregation");
  const fieldOptions = useMemo(
    () => options.filter((field) => !excludedFields.includes(field.name)).map((field) => ({
      label: field.name,
      value: field.name,
      type: field.type,
      qualified: isFieldQualified(field)
    })).sort(sortByLabel),
    [excludedFields, isFieldQualified, options]
  );
  const _onSelectAllRest = useCallback(
    () => onSelectAllRest(fieldOptions.map(({ value: fieldValue }) => fieldValue)),
    [fieldOptions, onSelectAllRest]
  );
  const _showSelectAllRest = !!(fieldOptions == null ? void 0 : fieldOptions.length) && showSelectAllRest && typeof _onSelectAllRest === "function";
  const _showDeSelectAll = showDeSelectAll && typeof onDeSelectAll === "function";
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Select,
    {
      options: fieldOptions,
      inputId: `select-${id}`,
      forwardedRef: selectRef,
      allowCreate,
      className,
      onMenuClose,
      openMenuOnFocus,
      persistSelection,
      clearable,
      placeholder,
      name,
      value,
      "aria-label": ariaLabel,
      optionRenderer: OptionRenderer,
      size,
      autoFocus,
      onChange,
      isLoading
    }
  ), (_showSelectAllRest || _showDeSelectAll) && /* @__PURE__ */ React.createElement(ButtonRow, null, _showSelectAllRest && /* @__PURE__ */ React.createElement(Button, { bsSize: "xs", onClick: _onSelectAllRest }, t("wizard_common.select_all_fields")), _showDeSelectAll && /* @__PURE__ */ React.createElement(Button, { bsSize: "xs", onClick: onDeSelectAll }, t("wizard_common.deselect_all_fields"))));
};
export default FieldSelect;
